import { Injectable } from '@angular/core';
import { StorageProvider } from '../sessions.service';

/**
 * Implementation of the LocalStorageProvider.
 * This class provides methods for storing and retrieving data in local storage.
 *
 * Note: Storing tokens in local storage is a common strategy, although it can pose security risks, such as:
 *  - Cross-Site Scripting (XSS) attacks, where an attacker injects malicious code to access stored tokens.
 *  - Cross-Site Request Forgery (CSRF) attacks, where an attacker tricks a user into performing an action to access stored tokens.
 *  - Persistence, where stored tokens remain even after the user logs out or closes the browser.
 *  - Lack of encryption, where stored tokens are not encrypted by default.
 *
 * Best practices to mitigate these risks include:
 *  - Using a secure token storage mechanism, such as cookies with httpOnly and secure flags.
 *  - Implementing token blacklisting to invalidate tokens when a user logs out or when a token is compromised.
 *  - Using short-lived tokens that expire after a short period.
 *  - Monitoring and logging token usage to detect potential security incidents.
 */
@Injectable()
export class LocalStorageProvider implements StorageProvider {
  /**
   * Sets a value in local storage.
   * @param key The key to store the value under.
   * @param value The value to store.
   */
  set(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  /**
   * Retrieves a value from local storage.
   * @param key The key to retrieve the value for.
   * @returns The stored value, or null if not found.
   */
  get(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Clears a value from local storage.
   * @param key The key to clear the value for.
   * @returns The cleared value, or null if not found.
   */
  clear(key: string): string | null {
    const lastValue = this.get(key);
    localStorage.removeItem(key);
    return lastValue;
  }
}
